import { format, parseISO } from 'date-fns'
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz'
import { nl } from 'date-fns/locale'

// Get the user's local timezone
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Format a date/time string to local timezone
export const formatToLocalTimezone = (
  dateString: string, 
  formatString: string = 'd MMM yyyy H:mm',
  timezone?: string
): string => {
  try {
    const userTimezone = timezone || getUserTimezone()
    const utcDate = parseISO(dateString)
    const localDate = utcToZonedTime(utcDate, userTimezone)
    return format(localDate, formatString, { locale: nl })
  } catch (error) {
    console.error('Error formatting date to local timezone:', error)
    // Fallback to original formatting
    const date = parseISO(dateString)
    return format(date, formatString, { locale: nl })
  }
}

// Format date only (no time) to local timezone
export const formatDateToLocal = (
  dateString: string,
  formatString: string = 'd MMM yyyy'
): string => {
  return formatToLocalTimezone(dateString, formatString)
}

// Format time only to local timezone
export const formatTimeToLocal = (
  dateString: string,
  formatString: string = 'H:mm'
): string => {
  return formatToLocalTimezone(dateString, formatString)
}

// Format full date and time to local timezone
export const formatDateTimeToLocal = (
  dateString: string,
  formatString: string = 'MMMM d, yyyy H:mm'
): string => {
  return formatToLocalTimezone(dateString, formatString)
}

// Format day of week with date to local timezone
export const formatDayDateToLocal = (
  dateString: string,
  formatString: string = 'EEEE, d MMMM yyyy'
): string => {
  try {
    const userTimezone = getUserTimezone()
    const utcDate = parseISO(dateString)
    const localDate = utcToZonedTime(utcDate, userTimezone)
    return format(localDate, formatString, { locale: nl })
  } catch (error) {
    console.error('Error formatting day date to local timezone:', error)
    // Fallback to original formatting
    const date = parseISO(dateString)
    return format(date, formatString, { locale: nl })
  }
}

// Format time range to local timezone
export const formatTimeRangeToLocal = (
  startDateString: string,
  endDateString: string,
  formatString: string = 'H:mm'
): string => {
  const startTime = formatTimeToLocal(startDateString, formatString)
  const endTime = formatTimeToLocal(endDateString, formatString)
  return `${startTime} - ${endTime}`
}

// Get timezone abbreviation (e.g., "EST", "PST")
export const getTimezoneAbbreviation = (): string => {
  const now = new Date()
  const timezone = getUserTimezone()
  
  try {
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    })
    const parts = formatter.formatToParts(now)
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')
    return timeZoneName?.value || timezone
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error)
    return timezone
  }
}

// Check if a date is today in local timezone
export const isTodayInLocal = (dateString: string): boolean => {
  try {
    const timezone = getUserTimezone()
    const utcDate = parseISO(dateString)
    const localDate = utcToZonedTime(utcDate, timezone)
    const today = utcToZonedTime(new Date(), timezone)
    
    return localDate.toDateString() === today.toDateString()
  } catch (error) {
    console.error('Error checking if date is today:', error)
    return false
  }
}

// Check if a date is upcoming in local timezone
export const isUpcomingInLocal = (dateString: string): boolean => {
  try {
    const timezone = getUserTimezone()
    const utcDate = parseISO(dateString)
    const localDate = utcToZonedTime(utcDate, timezone)
    const now = utcToZonedTime(new Date(), timezone)
    
    return localDate > now
  } catch (error) {
    console.error('Error checking if date is upcoming:', error)
    return false
  }
}

// Convert UTC datetime string to format expected by datetime-local input
export const utcToLocalDatetimeInput = (utcDateString: string): string => {
  try {
    const timezone = getUserTimezone()
    const utcDate = parseISO(utcDateString)
    const localDate = utcToZonedTime(utcDate, timezone)
    
    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    const year = localDate.getFullYear()
    const month = String(localDate.getMonth() + 1).padStart(2, '0')
    const day = String(localDate.getDate()).padStart(2, '0')
    const hours = String(localDate.getHours()).padStart(2, '0')
    const minutes = String(localDate.getMinutes()).padStart(2, '0')
    
    return `${year}-${month}-${day}T${hours}:${minutes}`
  } catch (error) {
    console.error('Error converting UTC to local datetime input:', error)
    // Fallback to original conversion
    return new Date(utcDateString).toISOString().slice(0, 16)
  }
}

// Convert UTC datetime string to separate date and time for dd/mm/yyyy format
export const utcToLocalDateAndTime = (utcDateString: string): { date: string, time: string } => {
  try {
    const timezone = getUserTimezone()
    const utcDate = parseISO(utcDateString)
    const localDate = utcToZonedTime(utcDate, timezone)
    
    // Format date as dd/mm/yyyy
    const day = String(localDate.getDate()).padStart(2, '0')
    const month = String(localDate.getMonth() + 1).padStart(2, '0')
    const year = localDate.getFullYear()
    
    // Format time as HH:mm
    const hours = String(localDate.getHours()).padStart(2, '0')
    const minutes = String(localDate.getMinutes()).padStart(2, '0')
    
    return {
      date: `${day}/${month}/${year}`,
      time: `${hours}:${minutes}`
    }
  } catch (error) {
    console.error('Error converting UTC to local date and time:', error)
    // Fallback
    const date = new Date(utcDateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    
    return {
      date: `${day}/${month}/${year}`,
      time: `${hours}:${minutes}`
    }
  }
}

// Convert local datetime input value back to UTC for database storage
export const localDatetimeInputToUtc = (localDatetimeString: string): string => {
  try {
    const timezone = getUserTimezone()
    // Parse the local datetime string (YYYY-MM-DDTHH:mm format)
    const localDate = new Date(localDatetimeString)
    // Convert to UTC using the user's timezone
    const utcDate = zonedTimeToUtc(localDate, timezone)
    return utcDate.toISOString()
  } catch (error) {
    console.error('Error converting local datetime input to UTC:', error)
    // Fallback to treating as UTC
    return new Date(localDatetimeString).toISOString()
  }
}

// Convert dd/mm/yyyy and HH:mm format back to UTC for database storage
export const localDateAndTimeToUtc = (dateString: string, timeString: string): string => {
  try {
    const timezone = getUserTimezone()
    
    // Parse dd/mm/yyyy format
    const [day, month, year] = dateString.split('/').map(Number)
    const [hours, minutes] = timeString.split(':').map(Number)
    
    // Create local date object
    const localDate = new Date(year, month - 1, day, hours, minutes)
    
    // Convert to UTC using the user's timezone
    const utcDate = zonedTimeToUtc(localDate, timezone)
    return utcDate.toISOString()
  } catch (error) {
    console.error('Error converting local date and time to UTC:', error)
    // Fallback - try to parse as regular date
    try {
      const [day, month, year] = dateString.split('/').map(Number)
      const [hours, minutes] = timeString.split(':').map(Number)
      const date = new Date(year, month - 1, day, hours, minutes)
      return date.toISOString()
    } catch (fallbackError) {
      console.error('Fallback date parsing also failed:', fallbackError)
      return new Date().toISOString()
    }
  }
}
